const AFFILIATE_ID_KEY = 'affiliate_id';
const AFFILIATE_CART_ATTRIBUTE_INPUT_NAME = `attributes[${AFFILIATE_ID_KEY}]`;
const AFFILIATE_LINE_ITEM_PROPERTY_INPUT_NAME = `properties[_${AFFILIATE_ID_KEY}]`;
const AFFILIATE_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

/**
 * @param {string} value
 */
function setAffiliateIdCookie(value) {
  const affiliateId = value.trim();
  if (!affiliateId) return;

  const cookieParts = [
    `${AFFILIATE_ID_KEY}=${encodeURIComponent(affiliateId)}`,
    'path=/',
    `max-age=${AFFILIATE_COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (window.location.protocol === 'https:') {
    cookieParts.push('Secure');
  }

  document.cookie = cookieParts.join('; ');
}

/**
 * Saves the affiliate_id URL parameter to a cookie when present.
 */
export function storeAffiliateIdFromUrl() {
  const affiliateId = new URL(window.location.href).searchParams.get(AFFILIATE_ID_KEY);
  if (affiliateId) {
    setAffiliateIdCookie(affiliateId);
  }
}

/**
 * @returns {string | null}
 */
export function getAffiliateId() {
  const cookiePrefix = `${AFFILIATE_ID_KEY}=`;
  const cookie = document.cookie.split('; ').find((cookiePart) => cookiePart.startsWith(cookiePrefix));
  if (!cookie) return null;

  const encodedValue = cookie.slice(cookiePrefix.length);
  try {
    return decodeURIComponent(encodedValue);
  } catch {
    return encodedValue;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} affiliateId
 */
function upsertAffiliateCartAttributeInput(form, affiliateId) {
  const existingInput = Array.from(form.elements).find((element) => {
    return element instanceof HTMLInputElement && element.name === AFFILIATE_CART_ATTRIBUTE_INPUT_NAME;
  });

  const input = existingInput instanceof HTMLInputElement ? existingInput : document.createElement('input');
  input.type = 'hidden';
  input.name = AFFILIATE_CART_ATTRIBUTE_INPUT_NAME;
  input.value = affiliateId;
  input.dataset.affiliateCartAttribute = 'true';

  if (!existingInput) {
    form.appendChild(input);
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} affiliateId
 */
function upsertAffiliateLineItemPropertyInput(form, affiliateId) {
  const existingInput = Array.from(form.elements).find((element) => {
    return element instanceof HTMLInputElement && element.name === AFFILIATE_LINE_ITEM_PROPERTY_INPUT_NAME;
  });

  const input = existingInput instanceof HTMLInputElement ? existingInput : document.createElement('input');
  input.type = 'hidden';
  input.name = AFFILIATE_LINE_ITEM_PROPERTY_INPUT_NAME;
  input.value = affiliateId;
  input.dataset.affiliateLineItemProperty = 'true';

  if (!existingInput) {
    form.appendChild(input);
  }
}

/**
 * Keeps product form hidden inputs in sync for checkout paths that serialize the form directly.
 *
 * @param {ParentNode | HTMLFormElement} [root]
 */
export function syncAffiliateCartAttributeInputs(root = document) {
  const affiliateId = getAffiliateId();
  if (!affiliateId) return;

  const forms =
    root instanceof HTMLFormElement
      ? [root]
      : root.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');

  for (const form of forms) {
    if (form instanceof HTMLFormElement) {
      upsertAffiliateCartAttributeInput(form, affiliateId);
      upsertAffiliateLineItemPropertyInput(form, affiliateId);
    }
  }
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, string>}
 */
function isAttributeRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Adds the affiliate cart attribute to an Ajax Cart update payload when available.
 *
 * @param {Record<string, any>} payload
 * @returns {Record<string, any>}
 */
export function withAffiliateCartAttributes(payload) {
  const affiliateId = getAffiliateId();
  if (!affiliateId) return payload;

  return {
    ...payload,
    attributes: {
      ...(isAttributeRecord(payload.attributes) ? payload.attributes : {}),
      [AFFILIATE_ID_KEY]: affiliateId,
    },
  };
}

/**
 * Persists the affiliate ID to Shopify cart attributes.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Record<string, any> | null>}
 */
export async function syncAffiliateIdToCart(options = {}) {
  const affiliateId = getAffiliateId();
  if (!affiliateId) return null;

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [AFFILIATE_ID_KEY]: affiliateId,
        },
      }),
      signal: options.signal,
    });

    if (!response.ok) return null;

    return await response.json();
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      console.error('Failed to sync affiliate cart attribute:', error);
    }

    return null;
  }
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isAcceleratedCheckoutEvent(event) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

  return path.some((target) => {
    return (
      target instanceof Element &&
      target.matches(
        'shopify-accelerated-checkout, shop-pay-wallet-button, .shopify-payment-button, .accelerated-checkout-block'
      )
    );
  });
}

storeAffiliateIdFromUrl();
syncAffiliateCartAttributeInputs();

document.addEventListener('submit', () => syncAffiliateCartAttributeInputs(), true);
document.addEventListener(
  'pointerdown',
  (event) => {
    if (isAcceleratedCheckoutEvent(event)) {
      syncAffiliateCartAttributeInputs();
    }
  },
  true
);
